import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../../lib/pb';
import type { CommentRecord, FeatureRecord } from '../../lib/types';
import { formatDate } from '../../i18n';
import { isAdmin, useStore } from '../../store/store';
import { addComment, deleteComment, setCommentResolved, type CommentTarget } from '../../store/actions';

export function CommentsTab({ feature: f }: { feature: FeatureRecord }) {
  return <CommentThread target={{ field: 'feature', id: f.id }} />;
}

/** Live comment thread for a map feature or a game-data record. */
export function CommentThread({ target }: { target: CommentTarget }) {
  const { t } = useTranslation();
  const user = useStore((s) => s.user);
  const users = useStore((s) => s.users);
  const [comments, setComments] = useState<CommentRecord[]>([]);
  const [body, setBody] = useState('');
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const filter = pb.filter(`${target.field} = {:f}`, { f: target.id });
    void pb
      .collection('comments')
      .getFullList<CommentRecord>({ filter, sort: 'created' })
      .then((list) => !cancelled && setComments(list));
    const unsub = pb.collection('comments').subscribe<CommentRecord>(
      '*',
      (e) => {
        setComments((cur) => {
          if (e.action === 'delete') return cur.filter((c) => c.id !== e.record.id);
          const i = cur.findIndex((c) => c.id === e.record.id);
          if (i < 0) return [...cur, e.record];
          const copy = cur.slice();
          copy[i] = e.record;
          return copy;
        });
      },
      { filter },
    );
    return () => {
      cancelled = true;
      void unsub.then((u) => u()).catch(() => undefined);
    };
  }, [target.field, target.id]);

  const visible = comments.filter((c) => showResolved || !c.resolved);
  const resolvedCount = comments.filter((c) => c.resolved).length;

  const post = async () => {
    if (!body.trim()) return;
    await addComment(target, body.trim());
    setBody('');
  };

  return (
    <div className="comments">
      {visible.length === 0 && <p className="muted">{t('comments.empty')}</p>}
      {visible.map((c) => {
        const mine = c.author === user?.id;
        return (
          <div key={c.id} className={`comment${c.resolved ? ' resolved' : ''}`}>
            <div className="comment-head small">
              <strong>{users[c.author]?.name ?? '—'}</strong>
              <span className="muted">{formatDate(c.created)}</span>
              <div className="spacer" />
              {(mine || isAdmin(user)) && (
                <>
                  <button className="link small" onClick={() => void setCommentResolved(c.id, !c.resolved)}>
                    {c.resolved ? t('comments.reopen') : t('comments.resolve')}
                  </button>
                  <button
                    className="link small danger"
                    onClick={() => {
                      if (confirm(t('comments.confirmDelete'))) void deleteComment(c.id);
                    }}
                  >
                    {t('common.delete')}
                  </button>
                </>
              )}
            </div>
            <div className="comment-body">{c.body}</div>
          </div>
        );
      })}
      {resolvedCount > 0 && (
        <button className="link small" onClick={() => setShowResolved(!showResolved)}>
          {showResolved ? t('comments.hideResolved') : t('comments.showResolved', { count: resolvedCount })}
        </button>
      )}
      <textarea
        rows={3}
        value={body}
        placeholder={t('comments.placeholder')}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void post();
        }}
      />
      <div className="save-row">
        <span className="muted small">{t('comments.ctrlEnter')}</span>
        <button className="primary" onClick={() => void post()} disabled={!body.trim()}>
          {t('comments.post')}
        </button>
      </div>
    </div>
  );
}
